/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2026-04-30
 * Description: Admin (superadmin context) view for API integration requests.
 *              Two tabs by request type (Documentation / Access), with a
 *              secondary status filter (pending / approved / rejected).
 *              Approval triggers a 6-digit verification email via ACS — the
 *              code is NOT returned to the admin; the requester verifies in
 *              their own browser.
 */

import React, { useEffect, useState, useCallback } from 'react'
import { api, AccessRequest } from '@services/api'
import { Plug, CheckCircle2, XCircle, Clock, RefreshCw, Mail, Smartphone } from 'lucide-react'
import { toast } from 'react-toastify'

type RequestType = 'documentation' | 'access'
type Status = 'pending' | 'approved' | 'rejected' | 'activity'

const STATUS_TABS: Status[] = ['pending', 'approved', 'rejected', 'activity']

const ApiIntegration: React.FC = () => {
  const [requestType, setRequestType] = useState<RequestType>('documentation')
  const [status, setStatus] = useState<Status>('pending')
  const [items, setItems] = useState<AccessRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [pendingActionId, setPendingActionId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.adminListAccessRequests({ status, type: requestType })
      setItems(data)
    } catch {
      toast.error('Failed to load requests')
    } finally {
      setLoading(false)
    }
  }, [status, requestType])

  useEffect(() => {
    load()
  }, [load])

  const handleApprove = async (req: AccessRequest) => {
    setPendingActionId(req.id)
    try {
      const result = await api.adminApproveAccessRequest(req.id)
      toast.success(
        `Approved — verification code emailed to ${req.email} (expires in ${result.expires_in_minutes} min)`,
      )
      load()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to approve'
      toast.error(msg)
    } finally {
      setPendingActionId(null)
    }
  }

  const handleReject = async (req: AccessRequest) => {
    const reason = window.prompt(`Reject ${req.request_type} request from ${req.email}? (optional reason):`, '')
    if (reason === null) return
    setPendingActionId(req.id)
    try {
      await api.adminRejectAccessRequest(req.id, reason || undefined)
      toast.success(`Rejected ${req.email}`)
      load()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to reject'
      toast.error(msg)
    } finally {
      setPendingActionId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Plug className="h-7 w-7 text-indigo-500" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">API Integration</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Approval queues for external developers requesting documentation viewing or live API access.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-900"
        >
          <RefreshCw className="mr-1.5 h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Top-level: request type */}
      <div className="grid grid-cols-2 gap-3">
        <TypeTab
          active={requestType === 'documentation'}
          onClick={() => setRequestType('documentation')}
          icon={<Mail className="h-5 w-5" />}
          title="Documentation Requests"
          subtitle="View Sonata + TMF Swagger UI"
        />
        <TypeTab
          active={requestType === 'access'}
          onClick={() => setRequestType('access')}
          icon={<Smartphone className="h-5 w-5" />}
          title="API Access Requests"
          subtitle="Live integration / API key issuance"
        />
      </div>

      {/* Secondary: status */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-6">
          {STATUS_TABS.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={
                'border-b-2 px-1 py-2 text-sm font-medium capitalize ' +
                (status === s
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300')
              }
            >
              {s}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="card py-12 text-center">
          <div className="border-primary-600 inline-block h-10 w-10 animate-spin rounded-full border-b-2"></div>
          <p className="mt-4 text-sm text-gray-400 dark:text-gray-500">Loading…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="card py-12 text-center text-sm text-gray-500 dark:text-gray-400">
          {status === 'activity' ? `No verified ${requestType} users yet.` : `No ${status} ${requestType} requests.`}
        </div>
      ) : status === 'activity' ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow dark:border-gray-700 dark:bg-gray-800">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <Th>Email</Th>
                <Th>Access type</Th>
                <Th>Auth method</Th>
                <Th>Last seen (local)</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {items.map((req) => (
                <tr key={req.id}>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{req.email}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {req.first_name} {req.last_name} · {req.company}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <span
                      className={
                        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ' +
                        (req.request_type === 'access'
                          ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                          : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300')
                      }
                    >
                      {req.request_type === 'access' ? (
                        <>
                          <Smartphone className="mr-1 h-3 w-3" /> API
                        </>
                      ) : (
                        <>
                          <Mail className="mr-1 h-3 w-3" /> Docs
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap text-gray-700 dark:text-gray-300">
                    {req.totp_enrolled_at ? 'authenticator app' : 'email-OTP'}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    {req.last_seen_at ? (
                      <LastSeen at={req.last_seen_at} />
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">never</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow dark:border-gray-700 dark:bg-gray-800">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <Th>Requester</Th>
                <Th>Company</Th>
                <Th>Use case</Th>
                <Th>Submitted</Th>
                <Th>State</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {items.map((req) => (
                <tr key={req.id}>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {req.first_name} {req.last_name}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">{req.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{req.company}</td>
                  <td className="max-w-xs px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    <div className="truncate" title={req.use_case ?? ''}>
                      {req.use_case || <span className="text-gray-400 dark:text-gray-500">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                    {new Date(req.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    <StateBadge req={req} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {req.status === 'pending' && (
                      <div className="flex justify-end space-x-2">
                        <button
                          disabled={pendingActionId === req.id}
                          onClick={() => handleApprove(req)}
                          className="inline-flex items-center rounded-md bg-green-600 px-2 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Approve
                        </button>
                        <button
                          disabled={pendingActionId === req.id}
                          onClick={() => handleReject(req)}
                          className="inline-flex items-center rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          <XCircle className="mr-1 h-3.5 w-3.5" /> Reject
                        </button>
                      </div>
                    )}
                    {req.status === 'approved' && !req.totp_enrolled_at && (
                      <button
                        disabled={pendingActionId === req.id}
                        onClick={() => handleApprove(req)}
                        title="Re-issue verification code email"
                        className="inline-flex items-center rounded-md border border-indigo-300 bg-white px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 dark:bg-gray-800 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
                      >
                        <Mail className="mr-1 h-3.5 w-3.5" /> Re-send code
                      </button>
                    )}
                    {req.status === 'rejected' && (
                      <div className="flex items-center justify-end space-x-1 text-xs text-gray-500 dark:text-gray-400">
                        <Clock className="h-3.5 w-3.5" />
                        {req.rejected_at && new Date(req.rejected_at).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <th
    className={
      'px-4 py-2 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400 ' +
      (className ?? '')
    }
  >
    {children}
  </th>
)

const TypeTab: React.FC<{
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  title: string
  subtitle: string
}> = ({ active, onClick, icon, title, subtitle }) => (
  <button
    onClick={onClick}
    className={
      'flex items-start gap-3 rounded-lg border p-4 text-left transition ' +
      (active
        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
        : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600')
    }
  >
    <span className={active ? 'text-indigo-600' : 'text-gray-500 dark:text-gray-400'}>{icon}</span>
    <span>
      <span
        className={
          'block text-sm font-semibold ' +
          (active ? 'text-indigo-900 dark:text-indigo-300' : 'text-gray-900 dark:text-gray-100')
        }
      >
        {title}
      </span>
      <span className="block text-xs text-gray-600 dark:text-gray-400">{subtitle}</span>
    </span>
  </button>
)

const LastSeen: React.FC<{ at: string }> = ({ at }) => {
  const d = new Date(at)
  const local = d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  // Relative hint, computed at render time.
  const diffMs = Date.now() - d.getTime()
  const mins = Math.floor(diffMs / 60_000)
  let rel: string
  if (mins < 1) rel = 'just now'
  else if (mins < 60) rel = `${mins}m ago`
  else if (mins < 1440) rel = `${Math.floor(mins / 60)}h ago`
  else rel = `${Math.floor(mins / 1440)}d ago`
  return (
    <div>
      <div className="text-gray-900 dark:text-gray-100">{local}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{rel}</div>
    </div>
  )
}

const StateBadge: React.FC<{ req: AccessRequest }> = ({ req }) => {
  if (req.status === 'pending') return <span className="text-amber-600">awaiting review</span>
  if (req.status === 'rejected') return <span className="text-red-600">rejected</span>
  // approved — sub-state based on verify / enroll progress
  if (req.totp_enrolled_at) return <span className="text-emerald-600">enrolled & active</span>
  if (req.verified_at) return <span className="text-blue-600">verified, awaiting TOTP</span>
  return <span className="text-indigo-600">code emailed</span>
}

export default ApiIntegration
